import type { AdapterOHLCVRequest, AdapterOptions, ExchangeAdapter } from './types';
import { ReconnectingSocket } from './ws';
import { candlesInWindow, decimalsOf, mapLimit, PAGE_CONCURRENCY, pageWindows, parseJson } from './binance';
import { ProviderError } from '../types';
import type { StreamStatus, Unsubscribe } from '../types';
import type { TradingPair } from '../../types/pair';
import type { OHLCV, Timeframe } from '../../types/ohlcv';
import type { Ticker24h } from '../../types/ticker';

/**
 * OKX v5 spot public market data (no API key; CORS echoes the page origin).
 * Endpoint facts this adapter relies on, verified live 2026-10-02:
 *   - GET /api/v5/market/history-candles?instId&bar&after&before&limit
 *     returns `[ts, open, high, low, close, vol (base), volCcy, volCcyQuote,
 *     confirm]` rows as strings, NEWEST first, strictly older than `after`
 *     and strictly newer than `before`, at most 300 per call (keeping the
 *     newest). It reaches back years and includes the forming candle, so
 *     /market/candles (latest 1440 rows only) is not needed.
 *   - It allows 20 requests per 2 s per IP; the 21st within 2 s gets HTTP 429.
 *   - 6H and longer bars default to Hong Kong time; the `…utc` variants
 *     (6Hutc 12Hutc 1Dutc 3Dutc 1Wutc 1Mutc) open on UTC boundaries: 1Wutc on
 *     Monday, 1Mutc on the 1st, 3Dutc on epoch-aligned 3-day buckets for
 *     every instrument checked — so every HopCharts timeframe is native.
 *   - Request errors (unknown instrument, bad bar) come back as HTTP 200 with
 *     a non-zero `code` and a `msg`.
 *   - instId is always BASE-QUOTE for spot.
 *   - Candle channels live on wss://ws.okx.com:8443/ws/v5/business, tickers
 *     on …/ws/v5/public. Idle connections are dropped after 30 s; the server
 *     answers a plain-text `ping` with `pong`.
 */
const REST_URL = 'https://www.okx.com/api/v5';
const CANDLE_STREAM_URL = 'wss://ws.okx.com:8443/ws/v5/business';
const TICKER_STREAM_URL = 'wss://ws.okx.com:8443/ws/v5/public';
const MAX_CANDLES = 300;
// Starts 125 ms apart: at most 17 history requests in any 2 s, under OKX's 20.
const CANDLE_REQUEST_SPACING_MS = 125;
const HEARTBEAT = { intervalMs: 25_000, message: 'ping' };

const BAR: Record<Timeframe, string> = {
  '1m': '1m',
  '3m': '3m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1H',
  '2h': '2H',
  '4h': '4H',
  '6h': '6Hutc',
  '12h': '12Hutc',
  '1D': '1Dutc',
  '3D': '3Dutc',
  '1W': '1Wutc',
  '1M': '1Mutc',
};

export function toOkxInstId(pair: string): string {
  return pair.trim().toUpperCase().replace('/', '-');
}

export function mapOkxCandles(rows: unknown): OHLCV[] {
  if (!Array.isArray(rows)) throw new ProviderError('Unrecognized OKX candle response', undefined, rows);
  const out: OHLCV[] = [];
  for (const row of rows) {
    if (!Array.isArray(row) || row.length < 6) continue;
    out.push({
      timestamp: Number(row[0]),
      open: Number(row[1]),
      high: Number(row[2]),
      low: Number(row[3]),
      close: Number(row[4]),
      volume: Number(row[5]),
    });
  }
  // Newest-first upstream; HopCharts wants oldest-first.
  return out.sort((a, b) => a.timestamp - b.timestamp);
}

interface OkxInstrument {
  baseCcy: string;
  quoteCcy: string;
  state: string;
  tickSz: string;
  lotSz: string;
}

interface OkxTicker {
  instId: string;
  last: string;
  open24h: string;
  high24h: string;
  low24h: string;
  vol24h: string;
  volCcy24h?: string;
  bidPx?: string;
  askPx?: string;
  ts: string;
}

interface OkxFrame {
  /** 'subscribe' acknowledges a subscription, 'error' rejects one. */
  event?: string;
  arg?: { channel?: string; instId?: string };
  data?: unknown;
}

/** Null when the market has no last price. */
function toTicker(pair: string, row: OkxTicker): Ticker24h | null {
  const last = Number(row.last);
  if (!(last > 0)) return null;
  const open = Number(row.open24h);
  const baseVolume = Number(row.vol24h);
  const quoteVolume = Number(row.volCcy24h);
  return {
    exchangeId: 'okx',
    pair,
    last,
    open24h: open,
    high24h: Number(row.high24h),
    low24h: Number(row.low24h),
    changePercent24h: open > 0 ? ((last - open) / open) * 100 : 0,
    baseVolume24h: baseVolume,
    quoteVolume24h: Number.isFinite(quoteVolume) ? quoteVolume : baseVolume * last,
    bid: row.bidPx ? Number(row.bidPx) : undefined,
    ask: row.askPx ? Number(row.askPx) : undefined,
    timestamp: Number(row.ts),
  };
}

export function okxAdapter(options: AdapterOptions = {}): ExchangeAdapter {
  let nextCandleRequestAt = 0;

  /** Resolves when the next history request may start (shared by every call on this adapter). */
  function candleRequestSlot(): Promise<void> {
    const now = Date.now();
    const at = Math.max(now, nextCandleRequestAt);
    nextCandleRequestAt = at + CANDLE_REQUEST_SPACING_MS;
    return at > now ? new Promise((resolve) => setTimeout(resolve, at - now)) : Promise.resolve();
  }

  async function getData(path: string): Promise<unknown> {
    const doFetch = options.fetch ?? globalThis.fetch;
    let response: Response;
    try {
      response = await doFetch(`${REST_URL}${path}`, { headers: { Accept: 'application/json' } });
    } catch (error) {
      throw new ProviderError(`OKX is unreachable: ${error instanceof Error ? error.message : String(error)}`);
    }
    const body = (await response.json().catch(() => null)) as { code?: string; msg?: string; data?: unknown } | null;
    if (!response.ok) {
      throw new ProviderError(body?.msg || `OKX request failed (${response.status})`, response.status, body);
    }
    // Request errors arrive as HTTP 200; there is no HTTP status to report for them.
    if (!body || body.code !== '0') throw new ProviderError(body?.msg || 'Unrecognized OKX response', undefined, body);
    return body.data;
  }

  /** One socket for `args`, subscribed again on every connect; `onData` gets each data push. */
  function openChannels(
    url: string,
    args: Array<{ channel: string; instId: string }>,
    onData: (arg: { channel?: string; instId?: string }, data: unknown[]) => void,
    onStatus?: (status: StreamStatus) => void,
  ): ReconnectingSocket {
    return new ReconnectingSocket({
      urls: [url],
      onOpen: (send) => send(JSON.stringify({ op: 'subscribe', args })),
      onMessage: (text) => {
        // `pong` is not JSON; subscribe acknowledgements carry `event: 'subscribe'`.
        const frame = parseJson(text) as OkxFrame | undefined;
        if (frame?.event === 'error') onStatus?.('error');
        else if (frame?.arg && Array.isArray(frame.data)) onData(frame.arg, frame.data);
      },
      onStatus,
      heartbeat: HEARTBEAT,
      WebSocket: options.WebSocket,
    });
  }

  return {
    id: 'okx',
    name: 'OKX',

    async getPairs(): Promise<TradingPair[]> {
      const data = await getData('/public/instruments?instType=SPOT');
      if (!Array.isArray(data)) throw new ProviderError('Unrecognized OKX instruments response', undefined, data);
      return (data as OkxInstrument[])
        .filter((i) => i.state === 'live')
        .map((i) => ({
          symbol: `${i.baseCcy}/${i.quoteCcy}`,
          base: i.baseCcy,
          quote: i.quoteCcy,
          exchangeId: 'okx',
          priceDecimals: decimalsOf(i.tickSz),
          quantityDecimals: decimalsOf(i.lotSz),
        }))
        .sort((a, b) => a.symbol.localeCompare(b.symbol));
    },

    async getOHLCV({ pair, timeframe, from, to }: AdapterOHLCVRequest): Promise<OHLCV[]> {
      const instId = toOkxInstId(pair);
      const bar = BAR[timeframe];
      if (!bar) throw new ProviderError(`Unsupported timeframe ${timeframe}`);
      const windows = pageWindows(from, to, timeframe, MAX_CANDLES);
      const pages = await mapLimit(windows, PAGE_CONCURRENCY, async ({ start, end }) => {
        await candleRequestSlot();
        // Both bounds are exclusive.
        const query = new URLSearchParams({
          instId,
          bar,
          after: String(end + 1),
          before: String(start - 1),
          limit: String(MAX_CANDLES),
        });
        return mapOkxCandles(await getData(`/market/history-candles?${query}`));
      });
      return candlesInWindow(pages, from, to);
    },

    async getTickers(): Promise<Ticker24h[]> {
      const data = await getData('/market/tickers?instType=SPOT');
      if (!Array.isArray(data)) throw new ProviderError('Unrecognized OKX ticker response', undefined, data);
      const tickers: Ticker24h[] = [];
      for (const row of data as OkxTicker[]) {
        const ticker = toTicker(row.instId.replace('-', '/'), row);
        if (ticker) tickers.push(ticker);
      }
      return tickers;
    },

    subscribeOHLCV(pair, timeframe, onCandle, onStatus): Unsubscribe {
      const channel = `candle${BAR[timeframe]}`;
      const socket = openChannels(
        CANDLE_STREAM_URL,
        [{ channel, instId: toOkxInstId(pair) }],
        (arg, rows) => {
          if (arg.channel !== channel) return;
          for (const candle of mapOkxCandles(rows)) onCandle(candle);
        },
        onStatus,
      );
      return () => socket.close();
    },

    subscribeTickers(pairs, onTicker): Unsubscribe {
      const pairByInstId = new Map(pairs.map((pair) => [toOkxInstId(pair), pair]));
      if (pairByInstId.size === 0) return () => {};
      const socket = openChannels(
        TICKER_STREAM_URL,
        [...pairByInstId.keys()].map((instId) => ({ channel: 'tickers', instId })),
        (arg, rows) => {
          if (arg.channel !== 'tickers') return;
          for (const row of rows as OkxTicker[]) {
            const pair = pairByInstId.get(row.instId);
            const ticker = pair ? toTicker(pair, row) : null;
            if (ticker) onTicker(ticker);
          }
        },
      );
      return () => socket.close();
    },
  };
}
